/**
 * Remix command vocabulary + the injected-dispatcher seam.
 *
 * Abhi's model: the state machine emits **output commands** on state entry/exit
 * ("input events in, output commands out"). A command is a fire-and-forget
 * announcement ("a remix started", "a remix errored"); handlers decide what to
 * do (toggle editor loading, open chat, fire analytics, cross a frame).
 *
 * Transport is NOT the machine's concern. The real command transport is
 * `@atlassian/rovo-content-bridge-api` — a client/command API that works both
 * same-context (local handlers) and cross-frame (iframe/webview) via pluggable
 * transports, using the identical `send`/`on` surface. Rather than couple this
 * pure state layer to that package, the machine dispatches through a minimal
 * injected `RemixCommandDispatcher`. Product wiring supplies an adapter that
 * maps these typed commands onto real bridge `createCommand` classes and
 * `api.send(...)`; tests/local usage can use the in-memory dispatcher below.
 *
 * This keeps `malleable-ui-components` transport-agnostic while using the real
 * bridge in production (option B).
 *
 * Additive; not wired into live flows.
 */

/** Discriminant for every remix command. */
export type RemixCommandType =
	| 'RemixStarted'
	| 'RemixReady'
	| 'RemixErrored'
	| 'RemixAborted'
	// The thinking/loading phase concluded (result ready, or it errored/aborted).
	// Used to clear editor loading state, independent of whether the flow lives
	// on (the user may still preview/accept a `ready` result).
	| 'RemixGenerationEnded'
	// The flow instance was fully torn down and removed from the store. Fires
	// exactly once, at the very end of a flow's life.
	| 'RemixFlowEnded';

/** Why a Remix flow was fully torn down and removed from the registry. */
/**
 * `abandoned`: a bounded handoff acknowledgement never arrived, so shadow ownership was released
 * without a terminal machine event. It is neither a user cancellation nor a product failure.
 */
export type RemixFlowEndReason =
	| 'inserted'
	| 'aborted'
	| 'cancelled'
	| 'error'
	| 'handed-off'
	| 'abandoned';

/** Why an adapter explicitly ended a Remix flow. */
export type RemixExplicitFlowEndReason = Exclude<RemixFlowEndReason, 'inserted'>;

/**
 * Fields common to every command.
 *
 * Commands are **self-describing announcements**: a consumer may be in another
 * frame, or registered long after the flow started, and has no access to the
 * machine's context. So every field a handler could need must travel on the
 * command itself — never assume the consumer can look it up.
 */
interface RemixCommandBase {
	remixId: string;
	conversationId: string;
	/**
	 * Node the flow is anchored to (mediaId / contentMauiId) — what a consumer
	 * should target when reflecting this flow in the UI (e.g. inline/embed
	 * loading). Undefined for an unanchored create, which has no node yet.
	 *
	 * On EVERY command deliberately: a handler reacting to `RemixGenerationEnded`
	 * must be able to clear the exact target `RemixStarted` set, and it cannot
	 * correlate by `remixId` without keeping its own map.
	 */
	targetNodeId?: string;
}

/** Command payloads keyed by type (discriminated union for exhaustive handling). */
export interface RemixCommandMap {
	RemixStarted: RemixCommandBase;
	RemixReady: RemixCommandBase & { resultId?: string };
	RemixErrored: RemixCommandBase;
	RemixAborted: RemixCommandBase;
	RemixGenerationEnded: RemixCommandBase & { reason: 'ready' | 'error' | 'aborted' };
	/**
	 * The flow instance was fully torn down and removed from the registry.
	 *
	 * `inserted` is emitted automatically when insertion completes. `aborted` and
	 * `cancelled` are supplied by the adapter during explicit teardown, after it
	 * has stopped any in-flight request and sent the corresponding terminal event.
	 */
	RemixFlowEnded: RemixCommandBase & { reason: RemixFlowEndReason };
}

/** A single command value: `{ type, ...payload }`. */
export type RemixCommand = {
	[K in RemixCommandType]: { type: K } & RemixCommandMap[K];
}[RemixCommandType];

/**
 * The seam the machine depends on. An adapter implements `dispatch` — either by
 * sending over the real bridge (`api.send(RemixStartedCommand, { ...payload,
 * senderId, receiverId })`) or, in tests/local, by fanning out to in-process
 * handlers via `InMemoryCommandDispatcher`.
 */
export interface RemixCommandDispatcher {
	dispatch(command: RemixCommand): void;
}

/** A handler receives every dispatched command (and narrows on `type`). */
export type RemixCommandHandler = (command: RemixCommand) => void;

/**
 * A default same-context dispatcher: a simple in-process fan-out to registered
 * handlers. Useful for tests, local wiring, and any surface that does not need a
 * frame hop. Mirrors the bridge's handler model (register → real unsubscribe).
 */
export class InMemoryCommandDispatcher implements RemixCommandDispatcher {
	private handlers = new Set<RemixCommandHandler>();

	register(handler: RemixCommandHandler): () => void {
		this.handlers.add(handler);
		return () => {
			this.handlers.delete(handler);
		};
	}

	dispatch(command: RemixCommand): void {
		for (const handler of Array.from(this.handlers)) {
			try {
				handler(command);
			} catch (error) {
				// Isolate handler failures from the state transition that emitted.
				// eslint-disable-next-line no-console
				console.error('[remix] command handler threw', command.type, error);
			}
		}
	}

	clear(): void {
		this.handlers.clear();
	}
}

/**
 * A process-wide default dispatcher, so consumers that do not inject one still
 * have a working same-context bus. Production surfaces that need cross-frame
 * delivery inject a bridge-backed dispatcher instead.
 */
export const defaultRemixCommandDispatcher: InMemoryCommandDispatcher =
	new InMemoryCommandDispatcher();

/** Convenience: register a handler on the default dispatcher. */
export const registerRemixCommandHandler = (handler: RemixCommandHandler): (() => void) =>
	defaultRemixCommandDispatcher.register(handler);

/** Convenience: dispatch on the default dispatcher. */
export const dispatchRemixCommand = (command: RemixCommand): void =>
	defaultRemixCommandDispatcher.dispatch(command);

/** Test helper: reset the default dispatcher. */
export const __resetRemixCommandBus = (): void => defaultRemixCommandDispatcher.clear();
